// d:\vvic-frontend\src\pages\Profile.js

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getProfile, updateProfile } from '../services/profile';
import { isAuthenticated } from '../services/auth';
import './Profile.css';

function Profile() {
	const [formData, setFormData] = useState({
		username: '',
		email: '',
		name: '',
		phone: '',
		postal_code: '',
		address: '',
		address_detail: '',
		customs_number: ''
	});
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState('');
	const [success, setSuccess] = useState('');
	const [isEditing, setIsEditing] = useState(false);
	const navigate = useNavigate();

	useEffect(() => {
		if (!isAuthenticated()) {
			alert('로그인이 필요합니다.');
			navigate('/login');
			return;
		}

		fetchProfile();
	}, [navigate]);

	const fetchProfile = async () => {
		try {
			setLoading(true);
			setError('');
			
			const data = await getProfile();
			
			console.log('프로필 데이터:', data);
			
			setFormData({
				username: data.username || '',
				email: data.email || '',
				name: data.name || '',
				phone: data.phone || '',
				postal_code: data.postal_code || '',
				address: data.address || '',
				address_detail: data.address_detail || '',
				customs_number: data.customs_number || ''
			});
		} catch (error) {
			console.error('프로필 로딩 실패:', error);
			
			if (error.response) {
				if (error.response.status === 401) {
					setError('로그인이 만료되었습니다. 다시 로그인해주세요.');
					setTimeout(() => navigate('/login'), 2000);
				} else if (error.response.status === 404) {
					setError('사용자 정보를 찾을 수 없습니다.');
				} else {
					setError(`서버 오류: ${error.response.status}`);
				}
			} else if (error.request) {
				setError('서버에 연결할 수 없습니다. 네트워크를 확인해주세요.');
			} else {
				setError('회원 정보를 불러오는데 실패했습니다.');
			}
		} finally {
			setLoading(false);
		}
	};

	const handleChange = (e) => {
		setFormData({
			...formData,
			[e.target.name]: e.target.value
		});
		setError('');
		setSuccess('');
	};

	const searchAddress = () => {
		new window.daum.Postcode({
			oncomplete: function(data) {
				setFormData(prev => ({
					...prev,
					postal_code: data.zonecode,
					address: data.address
				}));
			}
		}).open();
	};

	const validatePhone = (phone) => {
		const phoneRegex = /^01[0-9]-?[0-9]{3,4}-?[0-9]{4}$/;
		return phoneRegex.test(phone.replace(/-/g, ''));
	};

	const validateCustomsNumber = (number) => {
		const customsRegex = /^P[0-9]{12}$/;
		return customsRegex.test(number);
	};

	const handleEdit = () => {
		setIsEditing(true);
		setError('');
		setSuccess('');
	};

	const handleCancel = () => {
		setIsEditing(false);
		fetchProfile();
		setError('');
		setSuccess('');
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		setError('');
		setSuccess('');

		if (formData.phone && !validatePhone(formData.phone)) {
			setError('올바른 전화번호 형식이 아닙니다. (예: 010-1234-5678)');
			return;
		}

		if (formData.customs_number && !validateCustomsNumber(formData.customs_number)) {
			setError('올바른 개인통관고유부호 형식이 아닙니다. (예: P123456789012)');
			return;
		}

		setSaving(true);

		try {
			await updateProfile(formData);
			setSuccess('회원 정보가 수정되었습니다.');
			setIsEditing(false);
			
			const user = JSON.parse(localStorage.getItem('user'));
			if (user) {
				user.profile = {
					name: formData.name,
					phone: formData.phone,
					postal_code: formData.postal_code,
					address: formData.address,
					address_detail: formData.address_detail,
					customs_number: formData.customs_number
				};
				localStorage.setItem('user', JSON.stringify(user));
			}
			
			window.dispatchEvent(new Event('storage'));
			
			setTimeout(() => {
				setSuccess('');
			}, 3000);
		} catch (error) {
			console.error('프로필 수정 실패:', error);
			setError(error.response?.data?.error || '회원 정보 수정에 실패했습니다.');
		} finally {
			setSaving(false);
		}
	};

	const handleRetry = () => {
		fetchProfile();
	};

	if (loading) {
		return (
			<div className="profile-container">
				<div className="profile-box">
					<h2>회원 정보</h2>
					<div className="profile-loading" style={{
						textAlign: 'center',
						padding: '30px',
						fontSize: '16px',
						color: '#667eea'
					}}>
						⏳ 로딩 중...
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className="profile-container">
			<div className="profile-box" style={{ padding: '25px' }}>
				<h2 style={{ marginBottom: '20px' }}>회원 정보</h2>

				{error && (
					<div className="error-message" style={{ marginBottom: '15px', padding: '10px' }}>
						{error}
						<button onClick={handleRetry} className="btn-retry">
							다시 시도
						</button>
					</div>
				)}
				
				{success && <div className="success-message" style={{ marginBottom: '15px', padding: '10px' }}>{success}</div>}

				<form onSubmit={handleSubmit}>
					<div className="section" style={{ marginBottom: '20px' }}>
						<h3 style={{ marginBottom: '12px', fontSize: '16px' }}>기본 정보</h3>
						
						<div className="form-group" style={{ marginBottom: '12px' }}>
							<label style={{ marginBottom: '5px', fontSize: '14px' }}>아이디</label>
							<input
								type="text"
								value={formData.username}
								disabled
								className="disabled-input"
								style={{ padding: '8px' }}
							/>
						</div>

						<div className="form-group" style={{ marginBottom: '12px' }}>
							<label style={{ marginBottom: '5px', fontSize: '14px' }}>이메일</label>
							<input
								type="email"
								name="email"
								value={formData.email}
								onChange={handleChange}
								placeholder="이메일을 입력하세요"
								readOnly={!isEditing}
								style={{ padding: '8px', backgroundColor: !isEditing ? '#f5f5f5' : 'white', cursor: !isEditing ? 'not-allowed' : 'text' }}
							/>
						</div>

						<div className="form-group" style={{ marginBottom: '12px' }}>
							<label style={{ marginBottom: '5px', fontSize: '14px' }}>이름 <span className="required">*</span></label>
							<input
								type="text"
								name="name"
								value={formData.name}
								onChange={handleChange}
								placeholder="이름을 입력하세요"
								readOnly={!isEditing}
								style={{ padding: '8px', backgroundColor: !isEditing ? '#f5f5f5' : 'white', cursor: !isEditing ? 'not-allowed' : 'text' }}
							/>
						</div>

						<div className="form-group" style={{ marginBottom: '12px' }}>
							<label style={{ marginBottom: '5px', fontSize: '14px' }}>전화번호 <span className="required">*</span></label>
							<input
								type="tel"
								name="phone"
								value={formData.phone}
								onChange={handleChange}
								placeholder="010-1234-5678"
								readOnly={!isEditing}
								style={{ padding: '8px', backgroundColor: !isEditing ? '#f5f5f5' : 'white', cursor: !isEditing ? 'not-allowed' : 'text' }}
							/>
						</div>
					</div>

					<div className="section" style={{ marginBottom: '15px' }}>
						<h3 style={{ marginBottom: '12px', fontSize: '16px' }}>배송 정보</h3>

						<div className="form-group" style={{ marginBottom: '12px' }}>
							<label style={{ marginBottom: '5px', fontSize: '14px' }}>우편번호 <span className="required">*</span></label>
							<div className="input-with-button">
								<input
									type="text"
									name="postal_code"
									value={formData.postal_code}
									readOnly
									placeholder="우편번호"
									style={{ padding: '8px' }}
								/>
								<button 
									type="button" 
									onClick={searchAddress} 
									className="btn-search"
									disabled={!isEditing}
									style={{ 
										padding: '8px 15px',
										backgroundColor: !isEditing ? '#ccc' : '#667eea',
										cursor: !isEditing ? 'not-allowed' : 'pointer'
									}}
								>
									주소 검색
								</button>
							</div>
						</div>

						<div className="form-group" style={{ marginBottom: '12px' }}>
							<label style={{ marginBottom: '5px', fontSize: '14px' }}>기본주소 <span className="required">*</span></label>
							<input
								type="text"
								name="address"
								value={formData.address}
								readOnly
								placeholder="주소 검색 버튼을 클릭하세요"
								style={{ padding: '8px' }}
							/>
						</div>

						<div className="form-group" style={{ marginBottom: '12px' }}>
							<label style={{ marginBottom: '5px', fontSize: '14px' }}>상세주소 <span className="required">*</span></label>
							<input
								type="text"
								name="address_detail"
								value={formData.address_detail}
								onChange={handleChange}
								placeholder="상세주소를 입력하세요"
								readOnly={!isEditing}
								style={{ padding: '8px', backgroundColor: !isEditing ? '#f5f5f5' : 'white', cursor: !isEditing ? 'not-allowed' : 'text' }}
							/>
						</div>

						<div className="form-group" style={{ marginBottom: '12px' }}>
							<label style={{ marginBottom: '5px', fontSize: '14px' }}>개인통관고유부호 <span className="required">*</span></label>
							<input
								type="text"
								name="customs_number"
								value={formData.customs_number}
								onChange={handleChange}
								placeholder="P123456789012"
								maxLength="13"
								readOnly={!isEditing}
								style={{ padding: '8px', backgroundColor: !isEditing ? '#f5f5f5' : 'white', cursor: !isEditing ? 'not-allowed' : 'text' }}
							/>
							<p className="help-text" style={{ marginTop: '5px', fontSize: '12px' }}>
								개인통관고유부호를 정확히 입력하세요.
								<a href="https://unipass.customs.go.kr/csp/persIndex.do" target="_blank" rel="noopener noreferrer">
									발급받기
								</a>
							</p>
							
							{/* 🔥 개인통관고유부호 경고 문구 */}
							<div style={{
								marginTop: '10px',
								padding: '12px',
								backgroundColor: '#fff3cd',
								border: '2px solid #ffc107',
								borderRadius: '6px'
							}}>
								<p style={{
									color: '#dc3545',
									fontWeight: 'bold',
									fontSize: '13px',
									lineHeight: '1.5',
									margin: 0,
									marginBottom: '6px'
								}}>
									⚠️ <strong>개인통관고유부호 관련 중요 안내</strong>
								</p>
								<p style={{
									color: '#721c24',
									fontSize: '12px',
									lineHeight: '1.6',
									margin: 0
								}}>
									개인통관고유부호가 정확하지 않을 경우, 통관 과정에서 지연이 발생하거나 배송이 중단될 수 있습니다. 
									부정확한 정보로 인해 발생하는 통관 지연, 반송, 분실 등의 모든 책임은 고객님께 있으며, 
									이로 인한 환불 및 재배송은 불가능합니다. 반드시 정확한 정보를 입력해 주시기 바랍니다.
								</p>
							</div>
						</div>
					</div>

					{/* 🔥 버튼 그룹 - 항상 2개 표시 */}
					<div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
						<button 
							type="button" 
							onClick={isEditing ? handleCancel : handleEdit}
							disabled={saving}
							style={{ 
								flex: 1,
								padding: '12px',
								backgroundColor: isEditing ? '#6c757d' : '#667eea',
								color: 'white',
								border: 'none',
								borderRadius: '6px',
								fontSize: '15px',
								fontWeight: '600',
								cursor: saving ? 'not-allowed' : 'pointer'
							}}
						>
							{isEditing ? '취소' : '수정하기'}
						</button>
						<button 
							type="submit" 
							className="btn-submit" 
							disabled={!isEditing || saving || !!error}
							style={{ 
								flex: 1,
								padding: '12px',
								backgroundColor: (!isEditing || saving) ? '#ccc' : '#28a745',
								color: 'white',
								border: 'none',
								borderRadius: '6px',
								fontSize: '15px',
								fontWeight: '600',
								cursor: (!isEditing || saving) ? 'not-allowed' : 'pointer'
							}}
						>
							{saving ? '저장 중...' : '저장하기'}
						</button>
					</div>
				</form>
			</div>
		</div>
	);
}

export default Profile;